/**
 * "Your Coverage Assessment" — the projection, shared by guided planning and the D2C self-funding
 * sequence.
 *
 * The host computes the assessment (with `useFundingAssessment`, choosing its flow policy) and decides
 * what the forward choices are: guided planning offers one (insurance, Genworth's funding plan, or
 * plan comparison), the D2C sequence offers two (finish with self-funding, or explore insurance). The
 * screen renders each calculation state explicitly: loading, failed with retry, unavailable with the
 * reason, or the ready figures. Forward choices stay disabled until the figures are ready.
 */
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle, Button, Skeleton, cn } from '@design-system';

import { CoverageAssessmentResults } from '../components/coverage-assessment-results';
import type { FundingCalculation, FundingUnavailableReason } from '../lib/calculation-state';
import type { FundingAssessment } from '../lib/derive-funding-assessment';
import type { FundingScreenAction, FundingScreenFrame } from '../types';

export interface FundingAssessmentProjectionProps {
  assessment: FundingCalculation<FundingAssessment>;
  /** The forward choices, first one primary. Disabled automatically until the assessment is ready. */
  continueActions: readonly FundingScreenAction[];
  /** Ask about finding insurance under the gap (guided hides it when policy steps are skipped). */
  showInsurancePrompt: boolean;
  /** Tell a covered client they're well-positioned (default); a gap always shows. */
  showWellPositioned?: boolean;
  /** Fill the host's width (the consumer flow) instead of a centred column (guided planning). */
  fullWidth?: boolean;
  onBack?: () => void;
  frame: FundingScreenFrame;
}

const UNAVAILABLE_COPY: Record<FundingUnavailableReason, { title: string; body: string }> = {
  'no-complete-account': {
    title: 'Add at least one account to see your projection',
    body: 'Go back and finish the details for one of your accounts, then we can project your coverage.',
  },
  'missing-cost-estimate': {
    title: 'We can’t calculate this yet',
    body: 'Your long-term care cost estimate isn’t ready. Please try again in a moment.',
  },
  'missing-care-timing': {
    title: 'We can’t calculate this yet',
    body: 'We don’t yet know when care is likely to start. Please try again in a moment.',
  },
  'calculation-missing': {
    title: 'We can’t calculate this yet',
    body: 'Some of your account figures haven’t been calculated. Please try again in a moment.',
  },
};

function assessmentIcon(assessment: FundingCalculation<FundingAssessment>) {
  if (assessment.status !== 'ready') return undefined;
  const Icon = assessment.value.gap > 0 ? AlertTriangle : CheckCircle;
  return (
    <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full p-2">
      <Icon className="text-primary size-8" />
    </div>
  );
}

function ProjectionContent({
  assessment,
  showInsurancePrompt,
  showWellPositioned,
  fullWidth = false,
}: Pick<FundingAssessmentProjectionProps, 'assessment' | 'showInsurancePrompt' | 'showWellPositioned' | 'fullWidth'>) {
  const column = fullWidth ? 'w-full' : 'mx-auto max-w-3xl';
  switch (assessment.status) {
    case 'loading':
      return (
        <div className={cn(column, 'grid gap-4 md:grid-cols-2')}>
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      );
    case 'failed':
      return (
        <Alert
          variant="destructive"
          className={column}
        >
          <AlertTitle>We couldn’t calculate your coverage</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            Please try again in a moment.
            <Button
              variant="outline"
              size="sm"
              onClick={assessment.retry}
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      );
    case 'unavailable': {
      const copy = UNAVAILABLE_COPY[assessment.reason];
      return (
        <Alert className={column}>
          <AlertTitle>{copy.title}</AlertTitle>
          <AlertDescription>{copy.body}</AlertDescription>
        </Alert>
      );
    }
    case 'ready':
      return (
        <CoverageAssessmentResults
          assessment={assessment.value}
          showInsurancePrompt={showInsurancePrompt}
          showWellPositioned={showWellPositioned}
          fullWidth={fullWidth}
        />
      );
  }
}

export function FundingAssessmentProjection({
  assessment,
  continueActions,
  showInsurancePrompt,
  showWellPositioned,
  fullWidth,
  onBack,
  frame,
}: FundingAssessmentProjectionProps) {
  const isReady = assessment.status === 'ready';
  const [primary, secondary] = continueActions.map(action => ({
    ...action,
    disabled: !isReady || action.disabled,
  }));

  return frame({
    title: 'Your Coverage Assessment',
    subtitle: '',
    icon: assessmentIcon(assessment),
    content: (
      <ProjectionContent
        assessment={assessment}
        showInsurancePrompt={showInsurancePrompt}
        showWellPositioned={showWellPositioned}
        fullWidth={fullWidth}
      />
    ),
    primaryAction: primary,
    secondaryAction: secondary,
    backAction: onBack ? { label: 'Back', onSelect: onBack } : undefined,
  });
}
